import { useState } from 'react';
import PropTypes from 'prop-types';
import { get, filter, map } from 'lodash';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { formValueSelector } from 'redux-form';
import { NotificationBanner } from '../elements-banner/notification-banner';
import ReduxBanner from '../elements-banner/ReduxBanner';
import ListingImage from '../elements-image/ListingImageV2';
import ModalManager from '../elements-modal/modal-manager-v2';
import SelectField from '../app-main/components/reduxForms/fields/SelectField/SelectField';
import BaseRate from './BaseRate';
import DiscountRate from './DiscountRate';
import CleaningFee from './CleaningFee';
import FandB from './FandB';
import DateRate from './DateRate';
import { DateRateFormLeaveDialog } from './DateRateForm';
import CalendarRate from './CalendarRate';
import AttendeeRate from './AttendeeRate';
import DeletePricingModal from './modal/DeletePricingModal';
import { FANDB_FORM } from './forms/constants';
import { Link } from 'react-router';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { tocByUseType } from '../app-mylistings/data/utils';
import { useStatsigContext } from '../elements-statsig-provider/utils';

const EditPrice = ({
  listing = {},
  siblingListings = [],
  useType = '',
  isFandB,
  isListingFandB,
  intl,
  ...props
}) => {
  const [isDateRateFormDirty, setIsDateRateFormDirty] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const { featureGates } = useStatsigContext();
  const isDateBasedPricingEnabled = featureGates.date_based_pricing ?? false;
  const isFullServiceListing = listing.full_service?.active === true;
  const showDateBasedPricing = isDateBasedPricingEnabled && !isFullServiceListing;
  const currency = listing.prices?.[0]?.currency ?? '';
  const backLinkPath = `/mylistings/${listing._id}`;

  const handleBackClick = (e) => {
    if (isDateRateFormDirty) {
      e.preventDefault();
      setIsLeaveDialogOpen(true);
    }
  };
  const handleBackToEdit = () => setIsLeaveDialogOpen(false);
  const handleLeave = () => {
    setIsLeaveDialogOpen(false);
    props.dispatch(push(backLinkPath));
  };

  return (
    <div>
      <div className="ps-container-lg-margin ps-container-fixed">
        <div className="ps-row">
          <div className="col-sm-3 margin-lg-top">
            <div className="flex column flex-basis-50">
              <div className="margin-lg-bottom margin-xl-top">
                <Link to={backLinkPath} onClick={handleBackClick}>
                  <i className="icon-back ico-sm margin-sm-right" /><FormattedMessage id="hosting.edit_price.back_link_text" defaultMessage="Back"/>
                </Link>
              </div>
              <div className="flex middle">
                <div>
                  <ListingImage
                    sizes="(min-width: 768px) 40px, 80px"
                    imgId={get(listing, 'images.0.external_id', '')}
                    ratio={1}
                    widths={['40', '80', '120', '160']}
                  />
                </div>
                <div className="truncate text-small heavy margin-lg-left">{listing.title}</div>
              </div>
              <div className="margin-lg-top">
                <SelectField
                  input = {
                    {
                      name: 'use-type-select',
                      value: listing._id,
                      onChange: (listingId) => {
                        return props.dispatch(push(`/mylistings/${listingId}/price`));
                      }
                    }
                  }
                  options = {
                    map(siblingListings, (l) => {
                      const msg = get(tocByUseType, [l.use_type, 'optionLabel'], false);
                      return {
                        label: msg ? <FormattedMessage {...msg} /> : 'Off-Site' === l.use_type ? <FormattedMessage id='hosting.toc_by_use_type.option_label_off-site' defaultMessage='Meeting' /> : l.use_type,
                        value: l._id
                      };
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className="col-sm-8 col-sm-offset-1 margin-lg-top">
            <h2 className="header"><FormattedMessage id="hosting.edit_price.pricing_heading" defaultMessage="Pricing"/></h2>
            {
              ('Event' === useType && isListingFandB) &&
                <FandB listing={listing} isFandB={isFandB} {...props} />
            }
            { !isFandB && <BaseRate listing={listing} {...props} /> }
            {showDateBasedPricing && (
              <DateRate
                currency={currency}
                listingId={listing._id}
                onFormDirtyChange={setIsDateRateFormDirty}
              />
            )}
            <CalendarRate listing={listing} isFandB={isFandB} {...props} />
            <AttendeeRate listing={listing} {...props} />
            { !isFandB && <DiscountRate listing={listing} {...props} /> }
            { !isFandB && <CleaningFee listing={listing} {...props} />}
          </div>
        </div>
      </div>
      <ModalManager
        whichModal={get(props, 'modal.modalId', '')}
      >
        <DeletePricingModal id="DeletePricingModal" />
      </ModalManager>
      {showDateBasedPricing && (
        <DateRateFormLeaveDialog
          onBackToEdit={handleBackToEdit}
          onLeave={handleLeave}
          open={isLeaveDialogOpen}
        />
      )}
      <ReduxBanner>
        <NotificationBanner
          outerBannerClass="fixed-banner"
          bannerId="save"
          timing={2}
          message={
            <div className="flex middle center text-white">
              <div><i className="ico icon-yes" /></div>
              <div className="margin-md-left"><FormattedMessage id="hosting.edit_price.notification_banner_save_message" defaultMessage="Changes Saved"/></div>
            </div>
          }
        />
        <NotificationBanner
          outerBannerClass="fixed-banner"
          customClass="error-banner"
          timing={2}
          bannerId="error"
          message={
            <div className="flex middle center text-white">
              <div>
                <img className="ico warning-sign" src="/web-hosting/assets/images/warning-sign.svg?v=rb" alt={intl.formatMessage({ id: 'hosting.edit_price.warning_sign_image_alt', defaultMessage: 'warning sign' })} />
              </div>
              <div className="margin-md-left"><FormattedMessage id="hosting.edit_price.notification_banner_error_message" defaultMessage="Error. Changes could not be saved"/></div>
            </div>
          }
        />
      </ReduxBanner>
    </div>
  );
};

EditPrice.propTypes = {
  listings: PropTypes.object,
  params: PropTypes.object,
  toggleModal: PropTypes.func,
  dispatch: PropTypes.func,
  listing: PropTypes.object,
  listingFandB: PropTypes.bool,
  siblingListings: PropTypes.array,
  useType: PropTypes.string,
  isFandB: PropTypes.bool,
  intl: intlShape
};

const selectorFandB = formValueSelector(FANDB_FORM);
const mapStateToProps = (state) => {
  const { listings } = state;
  const listing = get(state, 'listing.data', {});
  const isPricePatching = get(state, 'listingPrice.isPatching', false);
  const listingPrice = get(state, 'listingPrice.data', {});
  const calendarPrice = get(state, 'calendarPrice.data', {});
  const listingsData = get(listings, 'data', []);
  const parentSpaceId = listing.parentSpaceId;
  const siblingListings = filter(listingsData, { parentSpaceId }, []);
  const useType = get(listing, 'use_type', '');
  const fandB = selectorFandB(state, 'fandb') || {};
  const fandBPrices = selectorFandB(state, 'prices') || {};
  const isFandB = (fandB.fandb_required || false).toString();
  const isListingFandB = get(listing, 'fandb_required', false);
  const modal = get(state, 'modal', {});
  const showVariablePricingForm = get(state, 'calendarPrice.showVariablePricingForm');
  const showAttendeePricingForm = get(state, 'calendarPrice.showAttendeePricingForm');

  return {
    listing,
    fandB,
    fandBPrices,
    isFandB: 'true' === isFandB,
    isListingFandB,
    siblingListings,
    useType,
    listingPrice,
    isPricePatching,
    calendarPrice,
    modal,
    showVariablePricingForm,
    showAttendeePricingForm
  };
};

export default connect(mapStateToProps)(injectIntl(EditPrice));
